import { Link } from 'react-router-dom';

import { correlationIdOf, isApiError } from '../api/errors';
import { Button, EmptyState } from './primitives';

/**
 * "We could not load this", as distinct from "this does not exist".
 *
 * WHY THIS COMPONENT EXISTS. Every detail screen destructured only
 * `{ data, isPending }` from its `useQuery` and never read `isError`, so a
 * failed fetch fell through to the same not-found empty state as a genuine 404:
 *
 *   > Booking not found — That booking reference does not match anything in the
 *   > system.
 *
 * That was observed live on BK-2608-0418 seconds after `POST /bookings`
 * returned 201. The database is remote (Railway over a public proxy) and
 * transient drops are known and expected — a P1017/P1001 class of failure
 * measured at ~3% under load. Telling staff a booking does not exist when the
 * network hiccuped is worse than showing nothing: the recovery it invites is to
 * create the booking again, which is a duplicate against a live one.
 *
 * The distinction is cheap to make correctly because the service layer already
 * maps 404 -> `null` (`orNullOn404`). So a rejected query is BY CONSTRUCTION
 * not a 404, and the two states never have to be guessed apart:
 *
 *   `data === null`  -> the record is genuinely absent   -> "not found"
 *   `isError`        -> we could not find out            -> this component
 */
export function LoadFailedState({
  noun,
  error,
  onRetry,
  isRetrying = false,
  backTo,
  backLabel,
}: {
  /** Lower-case singular, e.g. "booking". Reads as "Could not load this booking". */
  noun: string;
  error: unknown;
  onRetry: () => void;
  isRetrying?: boolean;
  backTo: string;
  backLabel: string;
}): JSX.Element {
  const correlationId = correlationIdOf(error);
  const status = isApiError(error) ? error.status : null;

  return (
    <EmptyState
      title={`Could not load this ${noun}`}
      description={
        // Deliberately says the record is probably FINE. The whole failure mode
        // being fixed is a user concluding their data is gone.
        `${describe(status)} This does not mean the ${noun} is missing — it means we could not reach it just now. Try again.` +
        (correlationId === null ? '' : ` Reference ${correlationId}.`)
      }
      action={
        <div className="flex items-center justify-center gap-3">
          <Button onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? 'Retrying…' : 'Try again'}
          </Button>
          <Link to={backTo}>
            <Button variant="outline">{backLabel}</Button>
          </Link>
        </div>
      }
    />
  );
}

/**
 * Name the failure without blaming the user's data.
 *
 * A 429 is worth calling out separately: it is the one case where "try again"
 * needs a beat of patience rather than an immediate second click, and the app
 * really is rate-limited rather than broken.
 */
function describe(status: number | null): string {
  if (status === 429) return 'The server is rate-limiting requests right now.';
  if (status !== null && status >= 500) return 'The server had a problem answering.';
  if (status === null) return 'The connection to the server failed.';
  return `The request failed (${String(status)}).`;
}
